/**
 * Purge Persisted Personal Data (ECHO-2318 / VAPT-006)
 *
 * Earlier builds persisted the full staff profile, the current patient, consultation content,
 * vital signs and the current doctor to localStorage, plus auth artefacts (PKCE verifier, OIDC
 * signin state). The stores no longer write that data, but existing browsers still hold it.
 * This runs on every boot (it is idempotent and cheap) to:
 * - reduce the persisted user profile to its non-identifying allowlist,
 * - remove the stores that are no longer persisted at all,
 * - remove legacy auth artefacts that now live in sessionStorage,
 * - remove the Mixpanel request queue, which held staff PII before batching was disabled, and
 * - replace a plaintext login id in the additional-info store with its fingerprint (async).
 *
 * Storage is injected so the purge is unit testable without a browser.
 */

import { toPersistedUserProfile } from "../stores/domains/user/persistedUserProfile.ts";
import type { UserProfile } from "../stores/types.ts";
import { fingerprintUserId, isUserIdFingerprint } from "./userIdentityFingerprint.ts";

/** Environment-isolated key/value store (keys are given without the environment prefix). */
export interface EnvKeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
  removeItem(key: string): unknown;
}

/** Raw Web Storage area, used for library keys that carry no environment prefix. */
export type RawStorageArea = Pick<Storage, "length" | "key" | "removeItem">;

export interface PurgePersonalDataOptions {
  /** Environment-isolated localStorage. */
  readonly store: EnvKeyValueStore;
  /** Raw localStorage, swept for legacy OIDC signin state. */
  readonly rawStore?: RawStorageArea;
  /** Additional environment-isolated keys to remove. */
  readonly extraKeys?: readonly string[];
  readonly onError?: (message: string, error: unknown) => void;
}

export const USER_STORE_KEY = "user-store";

export const ADDITIONAL_INFO_STORE_KEY = "user-additional-store";

/** Stores that used to be persisted and hold clinical data or staff PII. */
export const UNPERSISTED_STORE_KEYS = [
  "patient-store",
  "LegacyPatientStore",
  "echo-consultation-storage",
  "vital-signs-storage",
  "doctor-store",
] as const;

/** Key prefix of oidc-client-ts signin state, which used to default to localStorage. */
export const LEGACY_OIDC_KEY_PREFIX = "oidc.";

/**
 * Key prefix of the Mixpanel batched request queue (`__mpq_<token>_ev`, `_pp`, `_gr`). Batching is
 * disabled, so any unsent analytics left there is intentionally dropped.
 */
export const LEGACY_MIXPANEL_QUEUE_KEY_PREFIX = "__mpq_";

interface PersistedUserStore {
  state?: { profile?: UserProfile | null };
}

function sanitizePersistedUserStore(store: EnvKeyValueStore): void {
  const raw = store.getItem(USER_STORE_KEY);
  if (!raw) return;

  const persisted = JSON.parse(raw) as PersistedUserStore | null;
  const profile = persisted?.state?.profile;
  if (!persisted?.state || !profile) return;

  persisted.state.profile = toPersistedUserProfile(profile);
  store.setItem(USER_STORE_KEY, JSON.stringify(persisted));
}

interface PersistedAdditionalInfoStore {
  state?: { userId?: unknown };
}

function readLegacyPlaintextUserId(store: EnvKeyValueStore): string | null {
  const raw = store.getItem(ADDITIONAL_INFO_STORE_KEY);
  if (!raw) return null;

  const userId = (JSON.parse(raw) as PersistedAdditionalInfoStore | null)?.state?.userId;
  if (typeof userId !== "string" || !userId || isUserIdFingerprint(userId)) return null;
  return userId;
}

/**
 * Replace a plaintext login id (often an e-mail address) persisted by older builds with its
 * fingerprint, keeping the clinic/role selection. `saveUserSession` performs the same migration
 * on login; both write the same fingerprint, so a double write is harmless. The store is re-read
 * after hashing and only rewritten if it still holds that plaintext, so a newer write is never
 * overwritten.
 */
export async function migrateAdditionalInfoUserId(store: EnvKeyValueStore): Promise<void> {
  const plaintext = readLegacyPlaintextUserId(store);
  if (!plaintext) return;

  const fingerprint = await fingerprintUserId(plaintext);

  const raw = store.getItem(ADDITIONAL_INFO_STORE_KEY);
  const persisted = raw ? (JSON.parse(raw) as PersistedAdditionalInfoStore | null) : null;
  if (persisted?.state?.userId !== plaintext) return;

  persisted.state.userId = fingerprint;
  store.setItem(ADDITIONAL_INFO_STORE_KEY, JSON.stringify(persisted));
}

function removeKeysWithPrefix(area: RawStorageArea, prefix: string): void {
  const keys: string[] = [];
  for (let i = 0; i < area.length; i++) {
    const key = area.key(i);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  for (const key of keys) {
    area.removeItem(key);
  }
}

export function purgePersistedPersonalData({
  store,
  rawStore,
  extraKeys = [],
  onError,
}: PurgePersonalDataOptions): void {
  try {
    sanitizePersistedUserStore(store);
  } catch (error) {
    onError?.("Failed to sanitize the persisted user store", error);
  }

  for (const key of [...UNPERSISTED_STORE_KEYS, ...extraKeys]) {
    store.removeItem(key);
  }

  // Fire-and-forget: hashing is async and the purge must stay synchronous for the boot path.
  migrateAdditionalInfoUserId(store).catch((error: unknown) => {
    onError?.("Failed to migrate the persisted additional-info user id", error);
  });

  if (!rawStore) return;
  try {
    removeKeysWithPrefix(rawStore, LEGACY_OIDC_KEY_PREFIX);
  } catch (error) {
    onError?.("Failed to remove legacy OIDC state", error);
  }

  try {
    removeKeysWithPrefix(rawStore, LEGACY_MIXPANEL_QUEUE_KEY_PREFIX);
  } catch (error) {
    onError?.("Failed to remove the legacy Mixpanel request queue", error);
  }
}
